import { useCallback, useState } from 'react';
import { TableHeaders, Left, Right, Icon, TitleLast, CsvLink } from './styles';
import SVGIcon from '../../assets/images/svg/SVGIcon';
import MainTableComponent from '../maintable';
import Modal from '../modals/modal';
import ScreenShotModal from '../modals/screenShotModal';
import {
  ICON_SIZE,
  TAB_BUTTON_TYPE,
  TIME_REPORT_DATA_FILTER_ID,
  TIME_REPORT_VIEW_TYPE_ID
} from '../../global/constants';
import {
  ActivityDataInterface,
  DataItem,
  TableData,
  WorkSummaryInterface,
  ScreenshotListInterface
} from '../../interfaces/TimeReportInterface';
import { TimeReportCSVHeader, timeReportFilterOptions } from '../../global/row';
import { Dropdown, DropdownItem } from '../dropdown';
import Tabs from '../tabs';
import { rootStore } from '../../mobx/rootStore';
import { observer } from 'mobx-react-lite';

interface Props {
  timeSheetData: TableData;
  workSummaryData: WorkSummaryInterface[];
  activityData: ActivityDataInterface[];
  onChangeReportFilter: (propsName: string, value: number) => void;
}
function TableHeader({ timeSheetData, workSummaryData, activityData, onChangeReportFilter }: Props) {
  //State
  const [isStatusDropdownOpen, setIsStatusDropdownOpen] = useState(false);
  const [open, setOpen] = useState(false);
  const [selectedItem, setSelectedItem] = useState<ActivityDataInterface | null | DataItem>();

  // MobX store variable
  const { reportStore } = rootStore;
  const { reportFilter } = reportStore || {};
  const { selectedDataType } = reportFilter || {};

  const openStatusDropdown = useCallback((value: boolean) => {
    setIsStatusDropdownOpen(value);
  }, []);

  const handleOpen = useCallback((item: ActivityDataInterface | DataItem) => {
    setSelectedItem(item);
    setOpen(true);
  }, []);

  const handleClose = useCallback(() => {
    setOpen(false);
    setSelectedItem(null);
  }, []);

  // renders tab button
  const renderTabButton = useCallback(() => {
    return timeReportFilterOptions?.map((tab) => {
      return (
        <Tabs
          key={tab?.key}
          variant={TAB_BUTTON_TYPE.STANDARD}
          title={tab?.label}
          size='medium'
          isActive={selectedDataType === tab?.key}
          onClick={() => onChangeReportFilter('selectedDataType', tab?.key)}
        />
      );
    });
  }, [onChangeReportFilter, selectedDataType]);

  return (
    <div>
      <TableHeaders>
        <Left>{renderTabButton()}</Left>
        <Right>
          <Dropdown
            modalTitle='Filter'
            content={
              selectedDataType !== TIME_REPORT_DATA_FILTER_ID.ACTIVITYLOG && (
                <Icon role='button' onClick={() => openStatusDropdown(!isStatusDropdownOpen)}>
                  <SVGIcon
                    name='filters-icon'
                    width={ICON_SIZE.LG}
                    height={ICON_SIZE.LG}
                    viewBox={`0 0 ${ICON_SIZE.LG} ${ICON_SIZE.LG}`}
                    fill='var(--text-primary)'
                  />
                </Icon>
              )
            }
            trigger='click'>
            <DropdownItem
              label='By Project'
              onClick={() => onChangeReportFilter('timeViewType', TIME_REPORT_VIEW_TYPE_ID.BY_PROJECT)}
            />
            <DropdownItem
              label='By Member'
              onClick={() => onChangeReportFilter('timeViewType', TIME_REPORT_VIEW_TYPE_ID.BY_MEMBER)}
            />
            <DropdownItem
              label='By Activity'
              onClick={() => onChangeReportFilter('timeViewType', TIME_REPORT_VIEW_TYPE_ID.BY_ACTIVITY)}
            />
            <DropdownItem
              label='By Labels'
              onClick={() => onChangeReportFilter('timeViewType', TIME_REPORT_VIEW_TYPE_ID.BY_LABELS)}
            />
          </Dropdown>
          <CsvLink data={activityData || []} headers={TimeReportCSVHeader} filename={`time_log_report.csv`}>
            <TitleLast>Export as CSV</TitleLast>
          </CsvLink>
        </Right>
      </TableHeaders>
      {selectedDataType === TIME_REPORT_DATA_FILTER_ID.WORKSUMMARY && (
        <MainTableComponent
          showIcon={true}
          width
          columns={[
            {
              Header:
                reportFilter?.timeViewType === TIME_REPORT_VIEW_TYPE_ID.BY_PROJECT
                  ? 'Projects Name'
                  : reportFilter?.timeViewType === TIME_REPORT_VIEW_TYPE_ID.BY_MEMBER
                  ? 'Member Name'
                  : reportFilter?.timeViewType === TIME_REPORT_VIEW_TYPE_ID.BY_LABELS
                  ? 'Label Name'
                  : 'Activity Name',
              accessor: 'name'
            },
            { Header: 'Time', accessor: 'time' },
            { Header: 'Amount', accessor: 'amount' }
          ]}
          data={workSummaryData}
        />
      )}
      {selectedDataType === TIME_REPORT_DATA_FILTER_ID.TIMESHEET && (
        <MainTableComponent
          isScrollTable={true}
          isLastChildSticky={true}
          highlightLastRow={true}
          columns={timeSheetData?.column || []}
          data={timeSheetData?.data || []}
        />
      )}
      {selectedDataType === TIME_REPORT_DATA_FILTER_ID.ACTIVITYLOG && (
        <MainTableComponent
          isBorder={true}
          isBorderBottom={true}
          dataBorder={true}
          handleOpen={(item) => handleOpen(item)}
          isScrollTable={true}
          columns={[
            {
              Header: 'Date',
              accessor: 'date'
            },
            { Header: 'Time', accessor: 'time' },
            { Header: 'Activity Name', accessor: 'activityName' },
            { Header: 'Member', accessor: 'member' },
            { Header: 'Project Name', accessor: 'projectName' },
            { Header: 'Duration', accessor: 'duration' },
            { Header: 'Screenshots', accessor: 'screenshots' }
          ]}
          data={activityData}
        />
      )}
      <Modal open={open} onClose={handleClose} maxWidth='632px'>
        <ScreenShotModal
          taskName={selectedItem?.activityName as string}
          time={`${selectedItem?.projectName} (${selectedItem?.duration}, ${selectedItem?.date})`}
          mouseClicks='3829 mouse clicks'
          keyBoardHits='1101 keyboard hits'
          mainImage={(selectedItem?.screenshotImageList as ScreenshotListInterface[]) || []}
          modalClose={handleClose}
        />
      </Modal>
    </div>
  );
}

export default observer(TableHeader);
